import { cilHome } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import { CButton, CCol, CRow } from "@coreui/react";
import { ErrorMessage, Field, Form, Formik, FieldArray } from "formik";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import adminAxiosInstance, { imageAxiosInstance } from "../../../config";
import errorImage from "../../assets/errorImage.jpg";
import * as Yup from "yup";

import SweetAlert from "../../../helper/SweetAlert";
import { useNavigate } from "react-router-dom";

const PropertyEdit = () => {
  const [imageUrls, setImageUrls] = useState([]);
  const [data, setData] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const adminToken = localStorage.getItem("token");

  const propertyValidationSchema = Yup.object().shape({
    urlTitle: Yup.string().required("URL Title is required"),
    title: Yup.string().required("Title is required"),
    price: Yup.number().required("Price is required").min(0),
    propertyType: Yup.string().required("Property Type is required"),
    status: Yup.string().required("Status is required"),
    bedrooms: Yup.number().min(0),
    bathrooms: Yup.number().min(0),
  });

  const formattedId = id
    ?.trim()
    .toLowerCase()
    .replace(/\s+/g, "-");

  const fetchData = async () => {
    if (!id) return;
    try {
      const response = await adminAxiosInstance.get(`/property/getByUrl/${formattedId}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setData(response?.data?.data);
      if (response?.data?.data?.images) {
        setImageUrls(response.data.data.images);
      }
    } catch (error) {
      console.log("error", error);
      SweetAlert({
        icon: "error",
        text: error?.response?.data?.message || "Error fetching property details",
        title: "Error",
      });
    }
  };

const uploadImages = async (e) => {
  const files = Array.from(e.target.files);
  if (files.length === 0) return;

  setImageLoading(true);

  // Option 1: Upload one by one (recommended for now)
  const newUploadedImages = [];

  try {
    for (const file of files) {
      const formdata = new FormData();
      formdata.append("img", file);

      const response = await imageAxiosInstance.post("/upload", formdata, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const uploaded = response?.data?.data;

      if (uploaded?.mediaLink) {
        newUploadedImages.push({
          no: imageUrls.length + newUploadedImages.length + 1,
          url: uploaded.mediaLink,
          isPrimary: imageUrls.length === 0 && newUploadedImages.length === 0,
        });
      }
    }

    setImageUrls((prev) => [...prev, ...newUploadedImages]);
  } catch (error) {
    console.error("Upload error:", error);
    SweetAlert({
      icon: "error",
      text: "Error uploading images",
      title: "Error",
    });
  } finally {
    setImageLoading(false);
  }
};

  const removeImage = (index) => {
    const updatedImages = imageUrls.filter((_, i) => i !== index);
    setImageUrls(updatedImages);
  };

  const setPrimaryImage = (index) => {
    const updatedImages = imageUrls.map((img, i) => ({
      ...img,
      isPrimary: i === index,
    }));
    setImageUrls(updatedImages);
  };

  const handleFormSubmit = async (values) => {
    values.images = imageUrls;

    console.log("values", values);

    try {
      const url = id ? `/property/update/${values._id}` : "/property/add";
      const method = id ? "put" : "post";

      const response = await adminAxiosInstance[method](
        url,
        { ...values },
        {
          headers: { Authorization: `Bearer ${adminToken}` },
        }
      );
      SweetAlert({
        icon: "success",
        text: id ? "Property updated successfully!" : "Property created successfully!",
        title: "Success",
      });
      navigate("/admin/property");
    } catch (error) {
      console.log("error", error);
      SweetAlert({
        icon: "error",
        text: error?.response?.data?.message || "Error saving property",
        title: "Error",
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  return (
    <>
      <div className="d-flex align-items-center mb-3">
        <CIcon icon={cilHome} size="xxl" className="me-2" />
        <h3>{id ? "Edit Property" : "Edit Property"}</h3>
      </div>
      <div>
        <Link className="text-decoration-none text-white" to="/admin/property">
          <button className="text-white btn btn-info">Go Back</button>
        </Link>
      </div>

      <Formik
        enableReinitialize={true}
        initialValues={{
          _id: data?._id || "",
          urlTitle: data?.urlTitle || "",
          title: data?.title || "",
          price: data?.price || "",
          priceCurrency: data?.priceCurrency || "AED",
          propertyType: data?.propertyType || "Apartment",
          status: data?.status || "For Sale",
          bedrooms: data?.bedrooms || "",
          bathrooms: data?.bathrooms || "",
          maidRooms: data?.maidRooms || "",
          propertySizeSqft: data?.propertySizeSqft || "",
          propertySizeSqm: data?.propertySizeSqm || "",
          serviceChargesAED: data?.serviceChargesAED || "",
          availableFrom: data?.availableFrom || "",
          highlights: data?.highlights || [],
          amenities: data?.amenities || [],
          location: {
            address: data?.location?.address || "",
            latitude: data?.location?.latitude || "",
            longitude: data?.location?.longitude || "",
            community: data?.location?.community || "",
            subCommunity: data?.location?.subCommunity || "",
          },
          priceInsights: {
            averagePricePerSqft: data?.priceInsights?.averagePricePerSqft || "",
            comparisonToArea: data?.priceInsights?.comparisonToArea || "",
            similarPropertyComparison: data?.priceInsights?.similarPropertyComparison || "",
          },
          agentDetails: {
            name: data?.agentDetails?.name || "",
            agencyName: data?.agentDetails?.agencyName || "",
            agencyLicense: data?.agentDetails?.agencyLicense || "",
            agentLicense: data?.agentDetails?.agentLicense || "",
            phone: data?.agentDetails?.phone || "",
            whatsapp: data?.agentDetails?.whatsapp || "",
            email: data?.agentDetails?.email || "",
            responseTime: data?.agentDetails?.responseTime || "",
            rating: data?.agentDetails?.rating || "",
            totalProperties: data?.agentDetails?.totalProperties || "",
          },
          mortgage: {
            estimatedMonthlyPaymentAED: data?.mortgage?.estimatedMonthlyPaymentAED || "",
            interestRate: data?.mortgage?.interestRate || "",
            downPaymentAED: data?.mortgage?.downPaymentAED || "",
            loanAmountAED: data?.mortgage?.loanAmountAED || "",
            loanPeriodYears: data?.mortgage?.loanPeriodYears || "",
          },
          description: data?.description || "",
          paymentMethod: data?.paymentMethod || "Cash",
          regulatoryInformation: {
            reference: data?.regulatoryInformation?.reference || "",
            brokerLicense: data?.regulatoryInformation?.brokerLicense || "",
            agencyName: data?.regulatoryInformation?.agencyName || "",
            zoneName: data?.regulatoryInformation?.zoneName || "",
            dldPermitNumber: data?.regulatoryInformation?.dldPermitNumber || "",
            agentLicense: data?.regulatoryInformation?.agentLicense || "",
          },
        }}
        validationSchema={propertyValidationSchema}
        onSubmit={handleFormSubmit}
      >
        {({ values, isSubmitting, handleChange, handleBlur, setFieldValue }) => (
          <Form>
            {/* Basic Information */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Basic Information</h4>
              <CRow>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="urlTitle" className="fw-bold mb-2">
                      URL Title *
                    </label>
                    <Field
                      className="form-control"
                      name="urlTitle"
                      placeholder="e.g., vacant-marina-view-high-roi"
                      id="urlTitle"
                    />
                    <div className="text-danger">
                      <ErrorMessage name="urlTitle" />
                    </div>
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="title" className="fw-bold mb-2">
                      Title *
                    </label>
                    <Field
                      className="form-control"
                      name="title"
                      placeholder="e.g., Vacant | Marina View | High ROI"
                      id="title"
                    />
                    <div className="text-danger">
                      <ErrorMessage name="title" />
                    </div>
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="price" className="fw-bold mb-2">
                      Price *
                    </label>
                    <Field
                      type="number"
                      className="form-control"
                      name="price"
                      placeholder="3400000"
                      id="price"
                    />
                    <div className="text-danger">
                      <ErrorMessage name="price" />
                    </div>
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="propertyType" className="fw-bold mb-2">
                      Property Type *
                    </label>
                    <Field
                      as="select"
                      className="form-control"
                      name="propertyType"
                      id="propertyType"
                    >
                      <option value="apartment">Apartment</option>
                      <option value="plot">Plot</option>
                      <option value="villa">Villa</option>
                      <option value="townhouse">Townhouse</option>
                      <option value="penthouse">Penthouse</option>
                      <option value="office">Office</option>
                    </Field>
                    <div className="text-danger">
                      <ErrorMessage name="propertyType" />
                    </div>
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="status" className="fw-bold mb-2">
                      Status *
                    </label>
                    <Field
                      as="select"
                      className="form-control"
                      name="status"
                      id="status"
                    >
                      <option value="forSale">For Sale</option>
                      <option value="forRent">For Rent</option>
                      <option value="sold">Sold</option>
                      <option value="rented">Rented</option>
                    </Field>
                    <div className="text-danger">
                      <ErrorMessage name="status" />
                    </div>
                  </div>
                </CCol>
              </CRow>
            </div>

            {/* Property Details */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Property Details</h4>
              <CRow>
                <CCol sm={3}>
                  <div className="form-group mb-3">
                    <label htmlFor="bedrooms" className="fw-bold mb-2">
                      Bedrooms
                    </label>
                    <Field
                      type="number"
                      className="form-control"
                      name="bedrooms"
                      placeholder="3"
                      id="bedrooms"
                    />
                  </div>
                </CCol>
                <CCol sm={3}>
                  <div className="form-group mb-3">
                    <label htmlFor="bathrooms" className="fw-bold mb-2">
                      Bathrooms
                    </label>
                    <Field
                      type="number"
                      className="form-control"
                      name="bathrooms"
                      placeholder="2"
                      id="bathrooms"
                    />
                  </div>
                </CCol>
                <CCol sm={3}>
                  <div className="form-group mb-3">
                    <label htmlFor="maidRooms" className="fw-bold mb-2">
                      Maid Rooms
                    </label>
                    <Field
                      type="number"
                      className="form-control"
                      name="maidRooms"
                      placeholder="1"
                      id="maidRooms"
                    />
                  </div>
                </CCol>
                <CCol sm={3}>
                  <div className="form-group mb-3">
                    <label htmlFor="serviceChargesAED" className="fw-bold mb-2">
                      Service Charges (AED)
                    </label>
                    <Field
                      type="number"
                      className="form-control"
                      name="serviceChargesAED"
                      placeholder="15000"
                      id="serviceChargesAED"
                    />
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="propertySizeSqft" className="fw-bold mb-2">
                      Size (sqft)
                    </label>
                    <Field
                      type="number"
                      className="form-control"
                      name="propertySizeSqft"
                      placeholder="1500"
                      id="propertySizeSqft"
                    />
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="propertySizeSqm" className="fw-bold mb-2">
                      Size (sqm)
                    </label>
                    <Field
                      type="number"
                      className="form-control"
                      name="propertySizeSqm"
                      placeholder="139"
                      id="propertySizeSqm"
                    />
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="availableFrom" className="fw-bold mb-2">
                      Available From
                    </label>
                    <Field
                      type="date"
                      className="form-control"
                      name="availableFrom"
                      id="availableFrom"
                    />
                  </div>
                </CCol>
              </CRow>
            </div>

            {/* Location */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Location</h4>
              <CRow>
                <CCol sm={12}>
                  <div className="form-group mb-3">
                    <label htmlFor="location.address" className="fw-bold mb-2">
                      Address
                    </label>
                    <Field
                      className="form-control"
                      name="location.address"
                      placeholder="Murjan 1, Jumeirah Beach Residence, Dubai"
                      id="location.address"
                    />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="location.community" className="fw-bold mb-2">
                      Community
                    </label>
                    <Field
                      className="form-control"
                      name="location.community"
                      placeholder="Jumeirah Beach Residence"
                      id="location.community"
                    />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="location.subCommunity" className="fw-bold mb-2">
                      Sub Community
                    </label>
                    <Field
                      className="form-control"
                      name="location.subCommunity"
                      placeholder="Murjan 1"
                      id="location.subCommunity"
                    />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="location.latitude" className="fw-bold mb-2">
                      Latitude
                    </label>
                    <Field
                      type="number"
                      step="any"
                      className="form-control"
                      name="location.latitude"
                      placeholder="25.0772"
                      id="location.latitude"
                    />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="location.longitude" className="fw-bold mb-2">
                      Longitude
                    </label>
                    <Field
                      type="number"
                      step="any"
                      className="form-control"
                      name="location.longitude"
                      placeholder="55.1372"
                      id="location.longitude"
                    />
                  </div>
                </CCol>
              </CRow>
            </div>

            {/* Amenities */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Amenities</h4>
              <CRow>
                {["maidsRoom",
                    "balcony",
                    "security",
                    "builtInWardrobes",
                    "kitchenAppliances",
                    "sharedPool",
                    "centralAC",
                    "coveredParking",
                    "waterView",
                    "landmarkView",].map((amenity) => (
                  <CCol sm={4} key={amenity}>
                    <div className="form-check mb-2">
                      <Field
                        type="checkbox"
                        className="form-check-input"
                        name="amenities"
                        value={amenity}
                        id={`amenity-${amenity}`}
                      />
                      <label className="form-check-label" htmlFor={`amenity-${amenity}`}>
                        {amenity}
                      </label>
                    </div>
                  </CCol>
                ))}
              </CRow>
            </div>

            {/* Highlights */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Highlights</h4>
              <FieldArray name="highlights">
                {({ push, remove }) => (
                  <>
                    {values.highlights.map((highlight, index) => (
                      <CRow key={index} className="mb-2">
                        <CCol sm={10}>
                          <Field
                            className="form-control"
                            name={`highlights.${index}`}
                            placeholder="Enter highlight"
                          />
                        </CCol>
                        <CCol sm={2}>
                          <CButton
                            color="danger"
                            onClick={() => remove(index)}
                          >
                            Remove
                          </CButton>
                        </CCol>
                      </CRow>
                    ))}
                    <CButton
                      color="success"
                      onClick={() => push("")}
                    >
                      Add Highlight
                    </CButton>
                  </>
                )}
              </FieldArray>
            </div>

            {/* Agent Information */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Agent Information</h4>
              <CRow>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="agentDetails.name" className="fw-bold mb-2">
                      Agent Name
                    </label>
                    <Field
                      className="form-control"
                      name="agentDetails.name"
                      placeholder="John Doe"
                      id="agentDetails.name"
                    />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="agentDetails.agencyName" className="fw-bold mb-2">
                      Agency Name
                    </label>
                    <Field
                      className="form-control"
                      name="agentDetails.agencyName"
                      placeholder="Real Estate Agency"
                      id="agentDetails.agencyName"
                    />
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="agentDetails.phone" className="fw-bold mb-2">
                      Phone
                    </label>
                    <Field
                      className="form-control"
                      name="agentDetails.phone"
                      placeholder="+971 50 123 4567"
                      id="agentDetails.phone"
                    />
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="agentDetails.whatsapp" className="fw-bold mb-2">
                      WhatsApp
                    </label>
                    <Field
                      className="form-control"
                      name="agentDetails.whatsapp"
                      placeholder="+971 50 123 4567"
                      id="agentDetails.whatsapp"
                    />
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="agentDetails.email" className="fw-bold mb-2">
                      Email
                    </label>
                    <Field
                      type="email"
                      className="form-control"
                      name="agentDetails.email"
                      placeholder="agentDetails@example.com"
                      id="agentDetails.email"
                    />
                  </div>
                </CCol>
              </CRow>
            </div>

            {/* Images */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Property Images</h4>
              <div className="form-group mb-3">
                <label htmlFor="images" className="fw-bold mb-2">
                  Upload Images
                </label>
                <input
                  type="file"
                  id="images"
                  className="form-control"
                  multiple
                  onChange={uploadImages}
                  accept="image/*"
                  disabled={imageLoading}
                />
                {imageLoading && <small className="text-info">Uploading images...</small>}
              </div>
              <CRow>
                {imageUrls.map((img, index) => (
                  <CCol sm={3} key={index} className="mb-3">
                    <div className="position-relative">
                      <img
                        src={img.url || errorImage}
                        alt={`Property ${index + 1}`}
                        className="img-thumbnail w-100"
                        style={{ height: "200px", objectFit: "cover" }}
                      />
                      <div className="mt-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-danger me-2"
                          onClick={() => removeImage(index)}
                        >
                          Remove
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${img.isPrimary ? "btn-success" : "btn-outline-success"}`}
                          onClick={() => setPrimaryImage(index)}
                        >
                          {img.isPrimary ? "Primary" : "Set Primary"}
                        </button>
                      </div>
                    </div>
                  </CCol>
                ))}
              </CRow>
            </div>

            {/* Description */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Description</h4>
              <Field
                as="textarea"
                className="form-control"
                name="description"
                rows={6}
                placeholder="Enter property description..."
              />
            </div>

            {/* Submit Button */}
            <div className="mt-3">
              <CButton
                type="submit"
                className="btn btn-info text-white"
                disabled={isSubmitting || imageLoading}
              >
                {isSubmitting ? "Please wait..." : id ? "Update Property" : "Create Property"}
              </CButton>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default PropertyEdit;